import {
  MaximumFractionDigits,
  PrecisionFloat,
  getTargetFiatAndAssetAmount,
  intlFiatValueFormatting,
  getPrecisionFloatValue,
  getRoundedPrecision,
} from '@bts-web/utils-formatting';
import { getAppConfig, CURRENCY } from '@bts-web/core-app-config';
import { TradeControllerVariants, TradeOperation } from '../../utils';
import { MaximumTradeAmountType } from '../types';
import { AmountQuickSelectItem } from './../../../common/components/AmountQuickSelectButton/AmountQuickSelectButton';
import { OrderOperation } from '../../../orders/types';
import { ReadonlyURLSearchParams } from 'next/navigation';
import { TARGET_PRICE_KEY, PERCENTAGE_QUICK_SELECT_VALUES } from '../constants';

const createQuickSelectItem = (
  item: string,
  price: PrecisionFloat,
  opts: {
    currency: string;
    currentLocale: string;
  },
) => ({
  label: intlFiatValueFormatting(item, {
    locale: opts.currentLocale,
    currency: opts.currency,
  }),
  fiatAndAssetValues: getTargetFiatAndAssetAmount({
    sourceAmount: { value: item, precision: MaximumFractionDigits.FIAT },
    targetType: 'FIAT',
    price,
  }),
  value: `${item}`,
});

export type PercentageKey =
  | 'percentage25'
  | 'percentage50'
  | 'percentage75'
  | 'percentage100'
  | 'percentageMinus1'
  | 'percentageMinus5'
  | 'percentageMinus10'
  | 'percentagePlus1'
  | 'percentagePlus5'
  | 'percentagePlus10';

type TranslatedInnerValues = {
  percentage100: string;
  percentage75: string;
  percentage50: string;
  percentage25: string;
  percentageMinus1: string;
  percentageMinus5: string;
  percentageMinus10: string;
  market: string;
  percentagePlus1: string;
  percentagePlus5: string;
  percentagePlus10: string;
};

const percentageMap: Record<number | string, PercentageKey> = {
  '-1': 'percentageMinus1',
  '-5': 'percentageMinus5',
  '-10': 'percentageMinus10',
  '+1': 'percentagePlus1',
  '+5': 'percentagePlus5',
  '+10': 'percentagePlus10',
  25: 'percentage25',
  50: 'percentage50',
  75: 'percentage75',
  100: 'percentage100',
};

// Helper function to create percentage-based quick select items
export const createPercentageQuickSelectItem = (
  percentage: number,
  maximumTradeAmount: MaximumTradeAmountType,
  label: string,
  isSigned = false,
): AmountQuickSelectItem => {
  const assetPercent =
    (Number(getPrecisionFloatValue(maximumTradeAmount.ASSET)) *
      Math.abs(percentage)) /
    100;

  const assetValue = maximumTradeAmount.ASSET
    ? getRoundedPrecision({
        value: assetPercent.toString(),
        precision: MaximumFractionDigits.ASSET,
      })
    : '0';

  const fiatPercent =
    (Number(getPrecisionFloatValue(maximumTradeAmount.FIAT)) *
      Math.abs(percentage)) /
    100;

  let fiatValue = '0';

  if (maximumTradeAmount.FIAT) {
    fiatValue = getRoundedPrecision({
      value: fiatPercent.toString(),
      precision: MaximumFractionDigits.FIAT,
    });

    // If percentage is signed, we need to add/subtract the percentage from the provided amount -> needed for order target price quick select buttons
    if (isSigned) {
      fiatValue = getRoundedPrecision({
        value:
          percentage > 0
            ? (Number(maximumTradeAmount.FIAT.value) + fiatPercent).toString()
            : (Number(maximumTradeAmount.FIAT.value) - fiatPercent).toString(),
        precision: MaximumFractionDigits.FIAT,
      });
    }
  }

  return {
    label,
    fiatAndAssetValues: {
      FIAT: { value: fiatValue, precision: MaximumFractionDigits.FIAT },
      ASSET: { value: assetValue, precision: MaximumFractionDigits.ASSET },
    },
    value: `${percentage}%`,
  };
};

export const QUICK_SELECT_ITEMS_FOR_CURRENCY: Record<
  CURRENCY,
  Array<string>
> = {
  EUR: ['1', '50', '100'],
  GBP: ['1', '50', '100'],
  AED: ['50', '100'],
};

export const SAVINGS_QUICK_SELECT_ITEMS_FOR_CURRENCY: Record<
  CURRENCY,
  Array<string>
> = {
  EUR: ['10', '50', '100'],
  GBP: ['10', '50', '100'],
  AED: ['100'],
};

const getAssetPercentageSelectButtons = (
  maximumTradeAmount: MaximumTradeAmountType,
  translatedInnerValues: TranslatedInnerValues,
) =>
  PERCENTAGE_QUICK_SELECT_VALUES.map((pct) => {
    if (pct === 100) {
      return {
        label: translatedInnerValues.percentage100,
        fiatAndAssetValues: {
          FIAT: maximumTradeAmount['FIAT'],
          ASSET: maximumTradeAmount['ASSET'],
        },
        value: 'MAX',
      };
    }

    return createPercentageQuickSelectItem(
      pct,
      maximumTradeAmount,
      translatedInnerValues[percentageMap[pct]],
    );
  });

export const getTradeQuickSelectButtons = ({
  transactionType,
  currentLocale,
  price,
  maximumTradeAmount,
  translatedInnerValues,
  orderOperation,
  searchParams,
}: {
  price: PrecisionFloat;
  transactionType: TradeControllerVariants;
  orderOperation?: OrderOperation;
  currentLocale: string;
  maximumTradeAmount: MaximumTradeAmountType;
  translatedInnerValues: TranslatedInnerValues;
  searchParams?: ReadonlyURLSearchParams;
}): AmountQuickSelectItem[] => {
  const { currency } = getAppConfig();

  const isTargetPricePage = searchParams?.has(TARGET_PRICE_KEY);

  // Special handling for sell, swap and stake transaction type
  if (
    (transactionType === TradeOperation.sell && maximumTradeAmount) ||
    (transactionType === TradeOperation.swap && maximumTradeAmount) ||
    (transactionType === TradeOperation.stake && maximumTradeAmount) ||
    (transactionType === TradeOperation.unstake && maximumTradeAmount)
  ) {
    return getAssetPercentageSelectButtons(
      maximumTradeAmount,
      translatedInnerValues,
    );
  }

  if (transactionType === TradeOperation.order) {
    if (
      orderOperation === OrderOperation.SELL &&
      searchParams &&
      !isTargetPricePage
    ) {
      return getAssetPercentageSelectButtons(
        maximumTradeAmount,
        translatedInnerValues,
      );
    }

    return [
      createPercentageQuickSelectItem(
        100,
        getTargetFiatAndAssetAmount({
          sourceAmount: price,
          targetType: 'FIAT',
          price,
        }),
        translatedInnerValues.market,
      ),
      createPercentageQuickSelectItem(
        orderOperation === OrderOperation.SELL ? 1 : -1,
        getTargetFiatAndAssetAmount({
          sourceAmount: price,
          targetType: 'FIAT',
          price,
        }),
        translatedInnerValues[
          percentageMap[orderOperation === OrderOperation.SELL ? '+1' : '-1']
        ],
        true,
      ),
      createPercentageQuickSelectItem(
        orderOperation === OrderOperation.SELL ? 5 : -5,
        getTargetFiatAndAssetAmount({
          sourceAmount: price,
          targetType: 'FIAT',
          price,
        }),
        translatedInnerValues[
          percentageMap[orderOperation === OrderOperation.SELL ? '+5' : '-5']
        ],
        true,
      ),
      createPercentageQuickSelectItem(
        orderOperation === OrderOperation.SELL ? 10 : -10,
        getTargetFiatAndAssetAmount({
          sourceAmount: price,
          targetType: 'FIAT',
          price,
        }),
        translatedInnerValues[
          percentageMap[orderOperation === OrderOperation.SELL ? '+10' : '-10']
        ],
        true,
      ),
    ];
  }

  // Handle other transaction types (buy, savings)
  const baseQuickSelectItems =
    transactionType === TradeOperation.savings
      ? SAVINGS_QUICK_SELECT_ITEMS_FOR_CURRENCY[currency]
      : QUICK_SELECT_ITEMS_FOR_CURRENCY[currency];

  let quickSelectionItems = baseQuickSelectItems.map((item) =>
    createQuickSelectItem(item, price, { currentLocale, currency }),
  );

  // Add 500 for non-sell, non-swap types
  quickSelectionItems = [
    ...quickSelectionItems,
    createQuickSelectItem('500', price, { currentLocale, currency }),
  ];

  if (currency === 'AED' && transactionType === TradeOperation.savings) {
    // Add 1000 for savings
    quickSelectionItems = [
      ...quickSelectionItems,
      createQuickSelectItem('1000', price, { currentLocale, currency }),
    ];
  }

  return quickSelectionItems;
};
